import React, { useEffect, useRef, useState } from 'react';
import {
  PermissionSyncStatus,
  useGetPermissionSyncStatusQuery,
  useGetUserProfileQuery,
} from 'src/generated/graphql';
import { LoadingStatus } from 'src/common/ui/StatusScreen';
import ProfileUnavailablePage from 'src/components/auth/ProfileUnavailablePage';
import PermissionsUnavailablePage from 'src/components/auth/PermissionsUnavailablePage';
import {
  ClientWaitClocks,
  decideBootstrapAction,
  PERMISSION_SYNC_POLL_INTERVAL_MS,
} from 'src/components/auth/permissionSyncDecision';

/**
 * Bootstrap step that runs once authentication has succeeded and before any
 * page-level query is issued.
 *
 * It wraps the whole authenticated tree (see src/App.tsx), so nothing below it
 * mounts — and therefore nothing below it can issue a query — until the bootstrap
 * has finished. Wrapping the router rather than sitting inside it as a layout route
 * is what makes that structural: a route cannot be added outside the gate by
 * accident.
 *
 * Two steps, deliberately SEQUENTIAL:
 *
 * 1. `getUserProfile`. It has a backend side effect — it triggers the asynchronous
 *    permission-sync refresh for the caller — and that side effect, not the returned
 *    profile, is why the gate exists. Nothing below the gate consumes the response
 *    (the header reads the username from the JWT client-side); the gate only cares
 *    whether the call succeeded. That is why the query uses `network-only`: serving
 *    it from the in-memory Apollo cache on a remount would skip the side effect.
 *
 * 2. `getPermissionSyncStatus`, polled. It is `skip`ped until step 1 has returned,
 *    which is what enforces the ordering: reading the status before the trigger has
 *    fired would only ever report the pre-trigger state. It reports whether the
 *    caller has permissions worth rendering; the loader rules in
 *    {@link ./permissionSyncDecision} turn each reading into render / wait / give up.
 *
 * Both failure branches render in place of the children, at whatever URL the user is
 * on: the URL is where the user meant to be, and reloading it re-runs both steps,
 * which is what makes "refresh this page to try again" true.
 */
const AppBootstrap: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useGetUserProfileQuery({ fetchPolicy: 'network-only' });

  const profileReady = !profileError && !!profileData;

  const {
    data: statusData,
    error: statusError,
    startPolling,
    stopPolling,
  } = useGetPermissionSyncStatusQuery({
    // Enforces step 1 -> step 2 ordering: no status read until the trigger has
    // had its chance to fire.
    skip: !profileReady,
    // Every poll must reach the server. A cached read would return the same
    // answer forever and the loader would never exit — the same reason the
    // AppSync resolver must not be given a caching config.
    fetchPolicy: 'network-only',
  });

  // Forces a re-decision when a wait deadline passes. The decision is a function of
  // `Date.now()`, so without this the only thing that could end a wait is a poll
  // landing, which would make the grace window's real length a multiple of the poll
  // interval rather than the 5s it is specified as.
  const [deadlineTick, setDeadlineTick] = useState(0);

  // First-observation times for the two loader states with no server timestamp to
  // measure against (see permissionSyncDecision).
  //
  // Refs rather than state because they are inputs to the current decision, not
  // something to re-render on. Written during render, which is safe here because
  // every write is idempotent — set-once-if-unset, or clear — so StrictMode's
  // double-invoked render produces the same anchors as a single pass.
  const clocks = useRef<ClientWaitClocks>({
    waitWithoutServerStartSince: null,
    failedFirstSeenAt: null,
  });

  const snapshot = statusData?.getPermissionSyncStatus;
  const now = Date.now();

  if (snapshot && !snapshot.hasUnexpiredCachedPermissions) {
    const isFailed = snapshot.syncStatus === PermissionSyncStatus.Failed;
    const hasServerRunStartTime =
      snapshot.syncStatus === PermissionSyncStatus.InProgress &&
      typeof snapshot.syncStartedAt === 'number';

    // A FAILED that flips away and back starts a new grace window: the second
    // FAILED belongs to a different run than the first.
    clocks.current.failedFirstSeenAt = isFailed
      ? (clocks.current.failedFirstSeenAt ?? now)
      : null;

    // Only a wait with no server start time gets a client clock; a run that reports
    // when it started is measured against that instead.
    clocks.current.waitWithoutServerStartSince =
      isFailed || hasServerRunStartTime
        ? clocks.current.waitWithoutServerStartSince
        : (clocks.current.waitWithoutServerStartSince ?? now);
  }

  const decision = snapshot
    ? decideBootstrapAction(snapshot, now, clocks.current)
    : null;

  // Logging is a side effect, so it belongs in an effect keyed on the error rather
  // than in the render body: the app is wrapped in StrictMode (see src/index.tsx),
  // which double-invokes render, and the component renders again on every query
  // state transition. Keyed this way a given failure is reported once instead of
  // once per render pass.
  useEffect(() => {
    if (profileError) {
      console.error('[AppBootstrap] getUserProfile failed', profileError);
    }
  }, [profileError]);

  useEffect(() => {
    if (statusError) {
      console.error(
        '[AppBootstrap] getPermissionSyncStatus failed',
        statusError,
      );
    }
  }, [statusError]);

  // Poll only while the answer can still change what the gate shows. On RENDER a
  // later background refresh is invisible by design; on GIVE_UP the recovery is a
  // page refresh, not more polling. WAIT_EXTENDED deliberately keeps polling: it is
  // the state where refreshing cannot help, so the poll is the only thing that can
  // end it. Apollo owns the interval timer and clears it
  // when the query unmounts, and the cleanup here stops it on every transition, so
  // there is no interval left running behind a settled decision.
  //
  // A failed poll is deliberately NOT settling once a snapshot has been read: a wait
  // spans on the order of a hundred polls, so treating one transient failure as
  // terminal would strand users on "please refresh" mid-sync. Keep polling and let
  // the wait budget bound it — if the failures persist, the deadline still produces
  // row 5 rather than an indefinite loader.
  const settled =
    decision?.action === 'RENDER' || decision?.action === 'GIVE_UP';

  useEffect(() => {
    if (!profileReady || settled) {
      stopPolling();
      return;
    }
    startPolling(PERMISSION_SYNC_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [profileReady, settled, startPolling, stopPolling]);

  // One timer, for the current wait's deadline, replaced whenever the deadline moves
  // and cleared on unmount. Not an interval: a wait has exactly one moment at which
  // it expires, and re-deciding at any other time would produce the same answer.
  const reevaluateAt =
    decision?.action === 'WAIT' ? decision.reevaluateAt : null;
  useEffect(() => {
    if (reevaluateAt === null) {
      return;
    }
    const delay = Math.max(0, reevaluateAt - Date.now()) + 1;
    const timer = setTimeout(() => setDeadlineTick((tick) => tick + 1), delay);
    return () => clearTimeout(timer);
    // `deadlineTick` is a dependency so that a deadline which has already passed
    // (delay 0) cannot wedge: each tick re-runs the effect against a fresh decision.
  }, [reevaluateAt, deadlineTick]);

  if (profileLoading) {
    return <LoadingStatus>Loading your profile&hellip;</LoadingStatus>;
  }

  // `!profileData` shares the error branch rather than getting one of its own.
  // Nothing below the gate reads the profile — the header takes the username from
  // the JWT client-side — so this no longer narrows a dereference; it is the check
  // that the call the gate exists for actually returned something. A settled query
  // under Apollo's default `errorPolicy: 'none'` is either an error or data, so
  // "resolved with no profile" should not happen; if it ever does, the gate has not
  // confirmed the profile (nor, therefore, the permission-sync trigger) and must not
  // let the app through.
  if (!profileReady) {
    return <ProfileUnavailablePage />;
  }

  // The status read itself failing is not one of the loader rules. Fail closed only while
  // there is no answer at all: without one the gate cannot know whether the caller
  // has permissions, and letting the app through would render permission-filtered
  // pages that silently come back empty. Once a snapshot has been read, a later poll
  // failing is transient — keep showing the decision that snapshot produced.
  if (statusError && !decision) {
    return <PermissionsUnavailablePage />;
  }

  if (!decision) {
    // First status read still in flight. Brief by construction: one request, not a
    // wait.
    return <LoadingStatus>Checking your access&hellip;</LoadingStatus>;
  }

  if (decision.action === 'GIVE_UP') {
    return <PermissionsUnavailablePage />;
  }

  if (decision.action === 'WAIT') {
    return (
      <LoadingStatus>
        Syncing your permissions, please wait for a few minutes&hellip;
      </LoadingStatus>
    );
  }

  // Over budget but still running. Not an error: polling continues, so this resolves
  // itself if the run finishes and turns into the FAILED case if it does not.
  if (decision.action === 'WAIT_EXTENDED') {
    return (
      <LoadingStatus>
        Still syncing your permissions. This is taking longer than usual. Please
        come back and check after some time.
      </LoadingStatus>
    );
  }

  return <>{children}</>;
};

export default AppBootstrap;
